import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, RefreshCw, AlertTriangle, Settings2, Check, X, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

function fmtMoney(v, currency = "INR") {
  if (v === null || v === undefined) return "—";
  return `${currency === "INR" ? "₹" : ""}${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function ProfitabilityDashboard() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/profitability`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("You don't have permission to see financials");
      else toast.error("Failed to load");
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const t = data?.totals || {};
  const marginPositive = (t.margin || 0) >= 0;

  return (
    <div className="p-6 space-y-5" data-testid="profitability-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase text-muted-foreground tracking-wide flex items-center gap-1.5">
            {marginPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} Estimates & Contracts
          </div>
          <h1 className="text-3xl font-bold mt-0.5 tracking-tight">Profitability</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Actual timesheet cost rolled up against estimate revenue — see margin and cost overruns before they hurt.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => setSettingsOpen(true)} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5" data-testid="prof-settings-btn">
            <Settings2 className="w-3.5 h-3.5" /> Alert thresholds
          </button>
          <button type="button" onClick={load} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5" data-testid="prof-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {settingsOpen && (
        <ThresholdSettingsDialog
          wsId={activeWorkspaceId}
          clients={(data?.estimates || []).reduce((acc, r) => {
            if (r.client_id && !acc.find((x) => x.id === r.client_id)) acc.push({ id: r.client_id, name: r.client_name || r.client_id });
            return acc;
          }, [])}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <SumCard label="Revenue" value={fmtMoney(t.revenue)} />
        <SumCard label="Estimated cost" value={fmtMoney(t.estimated_cost)} />
        <SumCard label="Actual cost" value={fmtMoney(t.actual_cost)} accent={t.cost_overrun > 0 ? "rose" : null} />
        <SumCard label="Margin" value={fmtMoney(t.margin)} accent={marginPositive ? "emerald" : "rose"} sub={`${t.margin_pct || 0}%`} />
        <SumCard label="Cost overrun" value={fmtMoney(t.cost_overrun)} accent={t.cost_overrun > 0 ? "rose" : "emerald"} />
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs" data-testid="prof-table">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase text-muted-foreground tracking-wide">
              <th className="px-3 py-2">Estimate</th>
              <th className="px-3 py-2">Client / Brand</th>
              <th className="px-3 py-2 text-right">Revenue</th>
              <th className="px-3 py-2 text-right">Est. cost</th>
              <th className="px-3 py-2 text-right">Actual cost</th>
              <th className="px-3 py-2 text-right">Overrun</th>
              <th className="px-3 py-2 text-right">Margin</th>
              <th className="px-3 py-2 text-right">Margin %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data?.estimates || []).map((r) => {
              const overrun = r.cost_overrun > 0;
              const negMargin = r.margin < 0;
              return (
                <tr key={r.estimate_id} className="hover:bg-muted/20" data-testid={`prof-row-${r.estimate_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-[11px] text-muted-foreground">{r.number}</div>
                    <div className="font-medium">{r.title}</div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.client_name || "—"}
                    {r.brand_name && <span className="text-[10px] ml-1">/ {r.brand_name}</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.revenue)}</td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{fmtMoney(r.estimated_cost)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.actual_cost)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${overrun ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
                    {overrun && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                    {fmtMoney(r.cost_overrun)}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${negMargin ? "text-rose-600" : "text-emerald-700"}`}>
                    {fmtMoney(r.margin)}
                  </td>
                  <td className={`px-3 py-2 text-right ${negMargin ? "text-rose-600" : ""}`}>
                    {r.margin_pct}%
                  </td>
                </tr>
              );
            })}
            {(!data || data.estimates.length === 0) && !loading && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No approved/converted estimates yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SumCard({ label, value, sub, accent }) {
  const cls = accent === "rose" ? "text-rose-600" : accent === "emerald" ? "text-emerald-700" : "text-foreground";
  return (
    <div className="border border-border p-4 bg-background">
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className={`text-xl font-bold mt-1 font-mono ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ThresholdSettingsDialog({ wsId, clients, onClose }) {
  const [settings, setSettings] = useState({ overrun_threshold_pct: 0.80, per_client_thresholds: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newClientId, setNewClientId] = useState("");
  const [newClientPct, setNewClientPct] = useState("");

  useEffect(() => {
    api.get(`/workspaces/${wsId}/settings/profitability`)
      .then(({ data }) => setSettings({
        overrun_threshold_pct: data.overrun_threshold_pct ?? 0.80,
        per_client_thresholds: data.per_client_thresholds || {},
      }))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, [wsId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/settings/profitability`, {
        overrun_threshold_pct: Number(settings.overrun_threshold_pct),
        per_client_thresholds: Object.fromEntries(
          Object.entries(settings.per_client_thresholds).map(([k, v]) => [k, Number(v)])
        ),
      });
      toast.success("Thresholds saved");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };

  const addOverride = () => {
    if (!newClientId || !newClientPct) return;
    const pct = Number(newClientPct);
    if (!Number.isFinite(pct)) { toast.error("Invalid percentage"); return; }
    setSettings((s) => ({ ...s, per_client_thresholds: { ...s.per_client_thresholds, [newClientId]: pct } }));
    setNewClientId(""); setNewClientPct("");
  };
  const removeOverride = (cid) => {
    setSettings((s) => {
      const p = { ...s.per_client_thresholds };
      delete p[cid];
      return { ...s, per_client_thresholds: p };
    });
  };

  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || cid;
  const unusedClients = clients.filter((c) => !(c.id in (settings.per_client_thresholds || {})));

  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="prof-settings-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-background border border-border shadow-2xl">
        <div className="px-4 py-3 border-b border-border bg-muted/40 font-semibold text-sm flex items-center justify-between">
          <span>Cost overrun alert thresholds</span>
          <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        {loading ? (
          <div className="p-6 text-xs text-muted-foreground">Loading…</div>
        ) : (
          <div className="p-4 space-y-4 text-xs">
            <p className="text-muted-foreground">
              Estimates trigger a cost-overrun alert once actual timesheet cost reaches this fraction of the estimated cost. Default applies workspace-wide; per-client overrides beat the default.
            </p>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground tracking-wide">Workspace default</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number" step="0.05" min="0" max="5"
                  value={settings.overrun_threshold_pct}
                  onChange={(e) => setSettings({ ...settings, overrun_threshold_pct: e.target.value })}
                  className="w-24 text-xs px-2 py-1 bg-background border border-border"
                  data-testid="prof-settings-default"
                />
                <span className="text-muted-foreground">= {Math.round(Number(settings.overrun_threshold_pct || 0) * 100)}% of estimated cost</span>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide mb-1">Per-client overrides</div>
              {Object.keys(settings.per_client_thresholds || {}).length === 0 && (
                <div className="text-muted-foreground italic">No overrides — every client uses the workspace default.</div>
              )}
              <div className="space-y-1">
                {Object.entries(settings.per_client_thresholds || {}).map(([cid, pct]) => (
                  <div key={cid} className="flex items-center gap-2" data-testid={`prof-override-${cid}`}>
                    <span className="flex-1 truncate">{clientName(cid)}</span>
                    <input
                      type="number" step="0.05" min="0" max="5"
                      value={pct}
                      onChange={(e) => setSettings((s) => ({ ...s, per_client_thresholds: { ...s.per_client_thresholds, [cid]: e.target.value } }))}
                      className="w-20 text-xs px-2 py-1 bg-background border border-border"
                    />
                    <span className="text-muted-foreground text-[10px]">{Math.round(Number(pct || 0) * 100)}%</span>
                    <button type="button" onClick={() => removeOverride(cid)} className="p-1 text-muted-foreground hover:text-rose-500">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {unusedClients.length > 0 && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
                  <select value={newClientId} onChange={(e) => setNewClientId(e.target.value)} className="flex-1 text-xs px-2 py-1 bg-background border border-border" data-testid="prof-override-client">
                    <option value="">— Pick a client —</option>
                    {unusedClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <input
                    type="number" step="0.05" min="0" max="5"
                    placeholder="0.80"
                    value={newClientPct}
                    onChange={(e) => setNewClientPct(e.target.value)}
                    className="w-20 text-xs px-2 py-1 bg-background border border-border"
                    data-testid="prof-override-pct"
                  />
                  <button type="button" onClick={addOverride} className="text-xs px-2 py-1 border border-border hover:bg-muted flex items-center gap-1" data-testid="prof-override-add">
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving || loading} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium flex items-center gap-1" data-testid="prof-settings-save">
            <Check className="w-3 h-3" /> {saving ? "Saving…" : "Save thresholds"}
          </button>
        </div>
      </div>
    </div>
  );
}
